/*
 * The backend's global exception filter returns
 *   { statusCode, timestamp, path, method, message }
 * where `message` is a string or a string[]. A few endpoints also answer
 * HTTP 200 with { message: [...] } and no `results` to signal a failure.
 */

export class ApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const firstMessage = (value) => {
  if (Array.isArray(value)) return value.find((m) => typeof m === 'string' && m.trim()) || '';
  return typeof value === 'string' ? value : '';
};

export const DEFAULT_ERROR = 'Something went wrong. Please try again.';

export function getErrorMessage(error, fallback = DEFAULT_ERROR) {
  if (!error) return fallback;
  if (error instanceof ApiError && error.message) return error.message;

  const data = error.response?.data;
  const fromBody =
    firstMessage(data?.message) || firstMessage(Array.isArray(data) ? data : null) || firstMessage(data?.error);
  if (fromBody) return fromBody;

  if (error.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
  if (error.request && !error.response) {
    return typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'You appear to be offline. Check your connection and try again.'
      : 'We couldn’t reach Nappr. Please try again in a moment.';
  }
  return fallback;
}

export const getStatus = (error) => error?.status ?? error?.response?.status;

export const isNotFound = (error) => getStatus(error) === 404;

export const isForbidden = (error) => getStatus(error) === 403;
